import React, { forwardRef, useState } from 'react'
import {
  Dialog,
  DialogContent,
  IconButton,
  Grow,
  Grid,
  Typography,
  Button,
  Box,
} from '@mui/material'
import { motion } from 'framer-motion'
import { Close } from '@mui/icons-material'

import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { selectData } from 'layouts/onboarding'
import { useModal } from '../ModalProvider'

import { v4 as uuidv4 } from 'uuid'

const Transition = forwardRef(function Transition(props, ref) {
  return <Grow timeout={300} ref={ref} {...props} />
})

const PrivateLifeInsuranceEditor = ({ product, onChange, onRemove }) => {
  return (
    <Grid container columnSpacing={2} rowSpacing={1} mb={4}>
      <Grid item xs={6} sm={4} lg={6} xl={4} xxl={3}>
        {renderMuiFormField({
          label: 'Coverage Type',
          placeholder: 'Coverage Type',
          inputType: 'select',
          value: product.type,
          onChange: (e) => onChange('type', e),
          options: selectData.privateLifeInsuranceTypes,
        })}
      </Grid>
      <Grid item xs={6} sm={4} lg={6} xl={4} xxl={3}>
        {renderMuiFormField({
          label: 'Provider',
          placeholder: 'Provider',
          inputType: 'select',
          value: product.provider,
          onChange: (e) => onChange('provider', e),
          options: selectData.privateLifeInsuranceProviders,
        })}
      </Grid>
      <Grid item xs={6} sm={4} lg={6} xl={4} xxl={3}>
        {renderMuiFormField({
          label: 'Coverage Amount',
          placeholder: '$100,000.00',
          inputType: 'currencyFloat',
          value: product.coverage,
          onChange: (e) => onChange('coverage', e),
        })}
      </Grid>
      <Grid item xs={6} sm={4} lg={6} xl={4} xxl={3}>
        {renderMuiFormField({
          label: 'Monthly Premium',
          placeholder: '$250.00',
          inputType: 'currencyFloat',
          value: product.premium,
          onChange: (e) => onChange('premium', e),
        })}
      </Grid>
      {product.type !== 'whole_life' && (
        <Grid item xs={6} sm={4} lg={6} xl={4} xxl={3}>
          {renderMuiFormField({
            label: 'Years Remaining',
            placeholder: 'Years Remaining',
            inputType: 'int',
            value: product.yearsRemaining,
            onChange: (e) => onChange('yearsRemaining', e),
          })}
        </Grid>
      )}
      <Grid item xs={6} sm={4} lg={6} xl={4} xxl={3}>
        {renderMuiFormField({
          label: 'Owner',
          placeholder: 'Owner',
          inputType: 'select',
          value: product.owner,
          onChange: (e) => onChange('owner', e),
          options: selectData.productOwner,
        })}
      </Grid>
      {(product.type === 'gul_(guaranteed_universal_life)' ||
        product.type === 'iul_(indexed_universal_life)' ||
        product.type === 'whole_life') && (
        <Grid item xs={6} sm={4} lg={6} xl={4} xxl={3}>
          {renderMuiFormField({
            label: 'Cash Value',
            placeholder: '$2,500.00',
            inputType: 'currencyFloat',
            value: product.cashValue,
            onChange: (e) => onChange('cashValue', e),
          })}
        </Grid>
      )}

      {onRemove && (
        <IconButton onClick={onRemove} color="error" sx={{ alignSelf: 'end' }}>
          <RemoveCircleOutlineIcon />
        </IconButton>
      )}
    </Grid>
  )
}

export default function AddLifeInsurance({
  products = [],
  product: initialProduct,
  intakeState,
  updateIntakeStateAndSubmit,
  updateIntakeStateAtKey,
  onClose,
}) {
  const [product, setProduct] = useState(
    initialProduct || {
      provider: '',
      coverage: '',
      premium: '',
      type: '',
      owner: '',
      cashValue: '',
      yearsRemaining: '',
    }
  )

  const emptyProduct = {
    id: null,
    provider: '',
    coverage: '',
    premium: '',
    type: '',
    owner: '',
    cashValue: '',
    yearsRemaining: '',
  }

  const { openModal } = useModal()

  const resetForm = () => setProduct(emptyProduct)

  const handleChange = (field, value) => {
    setProduct({ ...product, [field]: value })
  }

  const handleSave = (addAnother = false) => {
    let updatedProducts

    if (product.id && products.some((p) => p.id === product.id)) {
      updatedProducts = products.map((p) =>
        p.id === product.id ? { ...product } : p
      )
    } else {
      updatedProducts = [...products, { ...product, id: uuidv4() }]
    }

    updateIntakeStateAndSubmit(() =>
      updateIntakeStateAtKey({ privateLifeInsurance: updatedProducts })
    )()

    if (addAnother) {
      onClose()
      resetForm()
      openModal('addLifeInsurance', {
        products: updatedProducts,
        updateIntakeStateAndSubmit,
        updateIntakeStateAtKey,
      })
    } else {
      onClose()
    }
  }

  const handleDelete = () => {
    const updatedProducts = products.filter((p) => p.id !== product.id)

    updateIntakeStateAndSubmit(() =>
      updateIntakeStateAtKey({ privateLifeInsurance: updatedProducts })
    )()

    onClose()
  }

  const handleCancel = () => {
    onClose()
  }

  return (
    <Dialog
      open
      onClose={handleCancel}
      TransitionComponent={Transition}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { overflow: 'visible' } }}
    >
      <DialogContent sx={{ overflow: 'visible' }}>
        <motion.div
          key="editor"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
          {/* Close button */}
          <Box
            sx={{
              justifyContent: 'space-between',
              position: 'relative',
              top: -64,
            }}
          >
            <IconButton
              onClick={handleCancel}
              sx={{
                position: 'absolute',
                top: 0,
                right: -16,
                color: '#fff',
                zIndex: 1,
                backgroundColor: '#000',
                '&:hover': { backgroundColor: '#000' },
              }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* Content */}
          <Box p={3}>
            <Box mb={3}>
              <Typography variant="headerTitle" sx={{ color: '#262627' }}>
                {initialProduct ? 'Edit Life Insurance' : 'Add Life Insurance'}
              </Typography>
            </Box>

            <PrivateLifeInsuranceEditor
              product={product}
              onChange={handleChange}
            />

            {/* Footer buttons */}
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              mt={{ xs: 1, md: 5 }}
            >
              <Box>
                <Button
                  onClick={handleCancel}
                  variant="parcoRounded"
                  color="outline"
                >
                  Cancel
                </Button>
              </Box>
              <Box sx={{ justifyContent: 'center' }}>
                {initialProduct && (
                  <Button
                    variant="parcoRounded"
                    color="outlineError"
                    onClick={handleDelete}
                  >
                    Delete
                  </Button>
                )}
                {!initialProduct && (
                  <Button
                    variant="parcoRounded"
                    onClick={() => handleSave(true)}
                  >
                    Save & Add Another
                  </Button>
                )}
                <Button
                  variant="parcoRounded"
                  onClick={() => handleSave(false)}
                  sx={{ marginLeft: '16px' }}
                >
                  Save
                </Button>
              </Box>
            </Box>
          </Box>
        </motion.div>
      </DialogContent>
    </Dialog>
  )
}
